import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AlertCircle, Check, Loader2, SendHorizontal, Sparkles, Square, Wand2 } from 'lucide-react';
import { getVerbaleChat } from '../../services/representativeService';
import { chiediAssistente, istantaneaVerbale } from '../../services/verbaliService';
import { chiaveSezione, dividiInSezioni, sostituisciSezione } from '../../utils/verbaleSezioni';
import { sanitizeHtml } from '../../utils/sanitizeHtml';

export interface PropostaSezione {
  titolo: string;
  html: string;
}

type StatoProposta = 'nuova' | 'in_corso' | 'applicata';

interface Messaggio {
  id: string;
  ruolo: 'utente' | 'assistente';
  testo?: string;
  html?: string;
  errore?: string;
  proposte?: (PropostaSezione & { stato: StatoProposta; erroreApplica?: string })[];
}

interface VerbaleAssistenteProps {
  eventId: string;
  /** HTML attuale del foglio (anche non ancora salvato). */
  contenuto: string;
  /** Riceve l'HTML intero del verbale con la proposta applicata. */
  onApplica: (html: string) => void | Promise<void>;
  /**
   * Salva il foglio (le modifiche in sospeso): prima di ogni domanda, così l'assistente legge
   * il testo attuale, e prima dell'istantanea di «Applica». false ⇒ non si chiede / non si applica.
   */
  primaDiApplicare?: () => Promise<boolean>;
  /** Generazione o impaginazione in corso: «Applica al verbale» resta disabilitato. */
  bloccato?: boolean;
}

const AZIONI_RAPIDE = [
  { etichetta: 'Controlla nomi e cariche', messaggio: 'Controlla nomi e cariche dei presenti e dei relatori nel verbale e segnala le incongruenze con i dati dell\'evento.' },
  { etichetta: 'Elenca le delibere', messaggio: 'Elenca le delibere prese nella riunione, con l\'esito di ciascuna.' },
  { etichetta: 'Rendi più sintetico', messaggio: 'Rendi più sintetico il verbale senza perdere fatti, decisioni e impegni.' },
  { etichetta: 'Aggiungi impegni', messaggio: 'Aggiungi alla sezione «Deliberazioni e impegni» gli impegni emersi nella riunione, ognuno con il responsabile.' },
];

const INTERROTTO = 'Interrotto, riprova';
const ERRORE_SALVATAGGIO_DOMANDA = 'Non sono riuscito a salvare il verbale: l\'assistente non leggerebbe le ultime modifiche. Riprova.';
const ERRORE_ISTANTANEA = 'Non sono riuscito a salvare una copia del verbale: la proposta non è stata applicata. Riprova.';

/** Testo della chat in text-sm: gli stili globali di p/li/h* altrimenti lo ingrandiscono. */
const TESTO_CHAT = 'text-sm leading-relaxed [&_p]:text-sm [&_p]:leading-relaxed [&_p]:mb-1.5 [&_p:last-child]:mb-0 '
  + '[&_li]:text-sm [&_ul]:list-disc [&_ul]:pl-4 [&_ul]:mb-1.5 [&_ol]:list-decimal [&_ol]:pl-4 [&_ol]:mb-1.5 '
  + '[&_h1]:text-sm [&_h2]:text-sm [&_h3]:text-sm [&_h4]:text-sm [&_h1]:font-semibold [&_h2]:font-semibold [&_h3]:font-semibold '
  + '[&_h1]:tracking-normal [&_h2]:tracking-normal [&_h3]:tracking-normal [&_h3]:mt-2 [&_h3]:mb-1 [&_strong]:font-semibold '
  + '[&_table]:w-full [&_table]:text-xs [&_td]:border [&_td]:border-slate-200 [&_td]:px-1.5 [&_th]:border [&_th]:border-slate-200 [&_th]:px-1.5';

const sembraHtml = (s: string) => /<\/?[a-z][\s\S]*>/i.test(s || '');

const escapeTitolo = (t: string) => String(t || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/** Sezione del verbale a cui va la proposta (per chiave del titolo), o null se non c'è. */
function sezioneDellaProposta(html: string, titolo: string) {
  const chiave = chiaveSezione(titolo);
  const sezioni = dividiInSezioni(html).filter((s) => s.chiave !== 'preambolo');
  return sezioni.find((s) => s.chiave === chiave)
    || sezioni.find((s) => chiaveSezione(s.titolo) === chiave)
    || null;
}

/** HTML del verbale con la proposta: solo quella sezione cambia; se manca, si aggiunge in fondo. */
export function applicaProposta(html: string, proposta: PropostaSezione): string {
  const sezione = sezioneDellaProposta(html, proposta.titolo);
  if (sezione) return sostituisciSezione(html, sezione.chiave, proposta.html);
  return `${html || ''}<h2>${escapeTitolo(proposta.titolo)}</h2>${proposta.html || ''}`;
}

/** Testo in streaming: fino al primo blocco di proposta (quello si mostra a fine risposta). */
const anteprimaStream = (testo: string) => {
  const dove = testo.search(/<proposta\b/i);
  return { visibile: dove >= 0 ? testo.slice(0, dove) : testo, conProposta: dove >= 0 };
};

let contatore = 0;
const nuovoId = () => { contatore += 1; return `m${Date.now()}-${contatore}`; };

const Proposta: React.FC<{
  proposta: NonNullable<Messaggio['proposte']>[number];
  prima: string | null;
  onApplica: () => void;
  bloccato?: boolean;
}> = ({ proposta, prima, onApplica, bloccato = false }) => {
  const [vista, setVista] = useState<'dopo' | 'prima'>('dopo');
  const html = vista === 'dopo' ? proposta.html : (prima ?? '');
  return (
    <div className="mt-2 overflow-hidden rounded-xl border border-emerald-200 bg-white dark:border-emerald-800 dark:bg-gray-900/60">
      <div className="flex items-center gap-2 border-b border-emerald-100 bg-emerald-50/70 px-3 py-2 dark:border-emerald-900 dark:bg-emerald-900/20">
        <Wand2 className="h-3.5 w-3.5 flex-shrink-0 text-emerald-700 dark:text-emerald-300" aria-hidden="true" />
        <span className="min-w-0 flex-1 truncate text-xs font-semibold text-emerald-900 dark:text-emerald-100">
          Proposta per «{proposta.titolo}»
        </span>
        <span className="flex flex-shrink-0 rounded-md bg-white p-0.5 text-xs ring-1 ring-emerald-200 dark:bg-gray-800 dark:ring-emerald-800" role="group" aria-label="Confronto">
          {(['prima', 'dopo'] as const).map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => setVista(v)}
              aria-pressed={vista === v}
              className={`rounded px-1.5 py-0.5 font-medium ${vista === v ? 'bg-emerald-600 text-white' : 'text-emerald-800 hover:bg-emerald-50 dark:text-emerald-200 dark:hover:bg-emerald-900/40'}`}
            >
              {v === 'prima' ? 'Prima' : 'Dopo'}
            </button>
          ))}
        </span>
      </div>
      <div className="max-h-56 overflow-y-auto px-3 py-2 font-serif text-slate-800 dark:text-gray-100">
        {vista === 'prima' && prima === null ? (
          <p className="text-xs italic text-slate-500 dark:text-gray-400">La sezione non c'è ancora: verrà aggiunta in fondo al verbale.</p>
        ) : html.trim() ? (
          <div className={TESTO_CHAT} dangerouslySetInnerHTML={{ __html: sanitizeHtml(html) }} />
        ) : (
          <p className="text-xs italic text-slate-500 dark:text-gray-400">Sezione vuota</p>
        )}
      </div>
      <div className="flex items-center justify-end gap-2 border-t border-emerald-100 px-3 py-2 dark:border-emerald-900">
        {proposta.erroreApplica && (
          <span className="mr-auto text-xs text-red-600 dark:text-red-400">{proposta.erroreApplica}</span>
        )}
        {proposta.stato === 'applicata' ? (
          <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 dark:text-emerald-300">
            <Check className="h-3.5 w-3.5" aria-hidden="true" /> Applicata al verbale
          </span>
        ) : (
          <button
            type="button"
            onClick={onApplica}
            disabled={proposta.stato === 'in_corso' || bloccato}
            title={bloccato ? 'Attendi la fine della generazione del verbale' : undefined}
            className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
          >
            {proposta.stato === 'in_corso' ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> : <Check className="h-3.5 w-3.5" aria-hidden="true" />}
            Applica al verbale
          </button>
        )}
      </div>
    </div>
  );
};

/** Assistente del verbale: chat che propone modifiche per sezione, da applicare al foglio. */
const VerbaleAssistente: React.FC<VerbaleAssistenteProps> = ({ eventId, contenuto, onApplica, primaDiApplicare, bloccato = false }) => {
  const [messaggi, setMessaggi] = useState<Messaggio[]>([]);
  const [caricamento, setCaricamento] = useState(true);
  const [input, setInput] = useState('');
  const [inCorso, setInCorso] = useState(false);
  const [stream, setStream] = useState('');
  const controllerRef = useRef<AbortController | null>(null);
  const elencoRef = useRef<HTMLDivElement>(null);
  const contenutoRef = useRef(contenuto);
  contenutoRef.current = contenuto;

  useEffect(() => {
    let attivo = true;
    setCaricamento(true);
    getVerbaleChat(eventId)
      .then((res) => {
        if (!attivo) return;
        const elenco = Array.isArray(res?.messages) ? res.messages : [];
        setMessaggi(elenco
          .filter((m: any) => m && typeof m.content === 'string' && m.content && m.content !== '__init__')
          .map((m: any) => (m.role === 'user'
            ? { id: nuovoId(), ruolo: 'utente' as const, testo: m.content }
            : sembraHtml(m.content)
              ? { id: nuovoId(), ruolo: 'assistente' as const, html: m.content }
              : { id: nuovoId(), ruolo: 'assistente' as const, testo: m.content })));
      })
      .catch(() => { /* cronologia non disponibile: si parte da vuota */ })
      .finally(() => { if (attivo) setCaricamento(false); });
    return () => { attivo = false; };
  }, [eventId]);

  useEffect(() => () => controllerRef.current?.abort(), []);

  useEffect(() => {
    const el = elencoRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messaggi, stream]);

  const invia = useCallback(async (testo: string) => {
    const messaggio = testo.trim();
    if (!messaggio || inCorso) return;
    setInput('');
    setMessaggi((prima) => [...prima, { id: nuovoId(), ruolo: 'utente', testo: messaggio }]);
    setInCorso(true);
    setStream('');
    const aggiungi = (m: Omit<Messaggio, 'id' | 'ruolo'>) =>
      setMessaggi((prima) => [...prima, { id: nuovoId(), ruolo: 'assistente', ...m }]);

    // Il server legge il verbale salvato: prima si salva il foglio, come prima di «Applica».
    if (primaDiApplicare) {
      let salvato = false;
      try { salvato = await primaDiApplicare(); } catch { salvato = false; }
      if (!salvato) {
        aggiungi({ errore: ERRORE_SALVATAGGIO_DOMANDA });
        setInCorso(false);
        return;
      }
    }

    const controller = new AbortController();
    controllerRef.current = controller;
    let chiuso = false;

    await chiediAssistente(eventId, messaggio, {
      delta: (pezzo) => setStream((prima) => prima + pezzo),
      fine: (dati) => {
        chiuso = true;
        const proposte = (Array.isArray(dati?.proposte) ? dati.proposte : [])
          .filter((p: any) => p && typeof p.titolo === 'string' && p.titolo.trim() && typeof p.html === 'string')
          .map((p: any) => ({ titolo: p.titolo.trim(), html: p.html, stato: 'nuova' as StatoProposta }));
        aggiungi({ html: typeof dati?.messaggio === 'string' ? dati.messaggio : '', proposte });
      },
      errore: (msg) => {
        chiuso = true;
        aggiungi({ errore: msg || INTERROTTO });
      },
    }, controller.signal);

    if (controllerRef.current === controller) controllerRef.current = null;
    if (!chiuso) aggiungi({ errore: INTERROTTO });
    setStream('');
    setInCorso(false);
  }, [eventId, inCorso, primaDiApplicare]);

  const aggiornaProposta = (mid: string, indice: number, modifica: Partial<NonNullable<Messaggio['proposte']>[number]>) =>
    setMessaggi((prima) => prima.map((m) => (m.id !== mid || !m.proposte ? m : {
      ...m,
      proposte: m.proposte.map((p, i) => (i === indice ? { ...p, ...modifica } : p)),
    })));

  const applica = async (mid: string, indice: number, proposta: PropostaSezione) => {
    if (bloccato) return;
    aggiornaProposta(mid, indice, { stato: 'in_corso', erroreApplica: undefined });
    try {
      if (primaDiApplicare && !(await primaDiApplicare())) throw new Error('salvataggio');
      await istantaneaVerbale(eventId, 'prima_di_applicare');
    } catch {
      aggiornaProposta(mid, indice, { stato: 'nuova', erroreApplica: ERRORE_ISTANTANEA });
      return;
    }
    try {
      await onApplica(applicaProposta(contenutoRef.current, proposta));
      aggiornaProposta(mid, indice, { stato: 'applicata' });
    } catch {
      aggiornaProposta(mid, indice, { stato: 'nuova', erroreApplica: 'Proposta non applicata, riprova.' });
    }
  };

  const { visibile: streamVisibile, conProposta } = anteprimaStream(stream);

  return (
    <section className="flex flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800" aria-label="Assistente verbale">
      <div className="flex items-center gap-2 border-b border-slate-200 px-4 py-3 dark:border-gray-700">
        <span className="grid h-7 w-7 place-items-center rounded-lg bg-gradient-to-br from-emerald-500 to-teal-500 text-white">
          <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
        </span>
        <span className="text-sm font-semibold text-slate-900 dark:text-white">Assistente verbale</span>
        <span className="ml-auto truncate text-xs text-slate-500 dark:text-gray-400">OpenAI · legge evento, presenze e verbale</span>
      </div>

      <div ref={elencoRef} className="flex h-80 flex-col gap-2.5 overflow-y-auto px-4 py-3">
        {caricamento ? (
          <div className="flex flex-1 items-center justify-center">
            <Loader2 className="h-4 w-4 animate-spin text-emerald-500" aria-hidden="true" />
          </div>
        ) : messaggi.length === 0 && !inCorso ? (
          <div className="m-auto max-w-xs text-center">
            <p className="text-sm font-medium text-slate-700 dark:text-gray-200">Chiedimi una modifica al verbale</p>
            <p className="mt-1 text-xs text-slate-500 dark:text-gray-400">
              Propongo il testo sezione per sezione: lo vedi prima e dopo e decidi tu se applicarlo.
            </p>
          </div>
        ) : null}

        {messaggi.map((m) => {
          if (m.ruolo === 'utente') {
            return (
              <div key={m.id} className="max-w-[90%] self-end whitespace-pre-wrap rounded-2xl rounded-br-md bg-emerald-600 px-3 py-2 text-sm text-white">
                {m.testo}
              </div>
            );
          }
          if (m.errore) {
            return (
              <div key={m.id} role="alert" className="flex max-w-[92%] items-start gap-2 rounded-2xl rounded-bl-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
                <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
                <span>{m.errore}</span>
              </div>
            );
          }
          return (
            <div key={m.id} className="max-w-[96%]">
              {(m.html || m.testo) && (
                <div className="rounded-2xl rounded-bl-md bg-slate-100 px-3 py-2 text-slate-800 dark:bg-gray-700/60 dark:text-gray-100">
                  {m.html
                    ? <div className={TESTO_CHAT} dangerouslySetInnerHTML={{ __html: sanitizeHtml(m.html) }} />
                    : <p className="whitespace-pre-wrap text-sm leading-relaxed">{m.testo}</p>}
                </div>
              )}
              {(m.proposte || []).map((p, i) => {
                const sezione = sezioneDellaProposta(contenuto, p.titolo);
                return (
                  <Proposta
                    key={`${m.id}-${i}`}
                    proposta={p}
                    prima={sezione ? sezione.html : null}
                    onApplica={() => applica(m.id, i, p)}
                    bloccato={bloccato}
                  />
                );
              })}
            </div>
          );
        })}

        {inCorso && (
          <div className="max-w-[96%] rounded-2xl rounded-bl-md bg-slate-100 px-3 py-2 text-slate-800 dark:bg-gray-700/60 dark:text-gray-100" aria-live="polite">
            {streamVisibile.trim()
              ? <div className={TESTO_CHAT} dangerouslySetInnerHTML={{ __html: sanitizeHtml(sembraHtml(streamVisibile) ? streamVisibile : streamVisibile.replace(/&/g, '&amp;').replace(/</g, '&lt;')) }} />
              : null}
            <p className="mt-1 flex items-center gap-1.5 text-xs text-slate-500 dark:text-gray-400">
              <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
              {conProposta ? 'Preparo una proposta per il verbale…' : 'Sto scrivendo…'}
            </p>
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-1.5 px-4 pb-2.5">
        {AZIONI_RAPIDE.map((a) => (
          <button
            key={a.etichetta}
            type="button"
            onClick={() => invia(a.messaggio)}
            disabled={inCorso}
            title={a.messaggio}
            className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 transition-colors hover:border-emerald-300 hover:bg-emerald-50 hover:text-emerald-800 disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:border-emerald-700 dark:hover:bg-emerald-900/30"
          >
            {a.etichetta}
          </button>
        ))}
      </div>

      <form
        className="flex items-end gap-2 border-t border-slate-200 px-4 py-3 dark:border-gray-700"
        onSubmit={(e) => { e.preventDefault(); invia(input); }}
      >
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value.slice(0, 20000))}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); invia(input); }
          }}
          rows={1}
          aria-label="Chiedi una modifica al verbale"
          placeholder="Chiedi una modifica al verbale…"
          className="max-h-32 min-h-[2.5rem] flex-1 resize-y rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
        />
        {inCorso ? (
          <button
            type="button"
            onClick={() => controllerRef.current?.abort()}
            aria-label="Interrompi"
            title="Interrompi"
            className="grid h-10 w-10 flex-shrink-0 place-items-center rounded-xl border border-slate-300 bg-white text-slate-700 hover:bg-slate-100 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
          >
            <Square className="h-4 w-4" aria-hidden="true" />
          </button>
        ) : (
          <button
            type="submit"
            aria-label="Invia"
            title="Invia"
            disabled={!input.trim()}
            className="grid h-10 w-10 flex-shrink-0 place-items-center rounded-xl bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40"
          >
            <SendHorizontal className="h-4 w-4" aria-hidden="true" />
          </button>
        )}
      </form>
    </section>
  );
};

export default VerbaleAssistente;
